import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, ArrowLeft } from "lucide-react";
import { CURRENCIES, VAT_RATES, UNITS, formatMoney } from "@/lib/format";
import { toast } from "sonner";

const CFG = {
  sales: { endpoint: "/sales-invoices", entityEndpoint: "/customers", entityLabel: "Müşteri", listRoute: "/satis-faturalari", title: "Satış Faturası" },
  purchase: { endpoint: "/purchase-invoices", entityEndpoint: "/suppliers", entityLabel: "Tedarikçi", listRoute: "/alis-faturalari", title: "Alış Faturası" },
};

export default function InvoiceForm({ kind }) {
  const cfg = CFG[kind];
  const nav = useNavigate();
  const location = useLocation();
  const backTo = location.state?.returnTo || cfg.listRoute;
  const { id } = useParams(); // if present -> edit
  const [searchParams] = useSearchParams();
  const presetEntityId = searchParams.get("entity_id") || "";
  const presetProjectId = searchParams.get("project_id") || "";
  const prefillItems = location.state?.prefillItems;
  const [entities, setEntities] = useState([]);
  const [products, setProducts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [entityId, setEntityId] = useState(presetEntityId);
  const [projectId, setProjectId] = useState(presetProjectId);
  const [currency, setCurrency] = useState("TRY");
  const [date, setDate] = useState(new Date().toISOString().slice(0,16));
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState(
    Array.isArray(prefillItems) && prefillItems.length > 0
      ? prefillItems.map(it => ({
          product_id: null,
          project_item_id: it.project_item_id || null,
          name: it.name || "",
          quantity: Number(it.quantity || 1),
          unit: it.unit || "adet",
          unit_price: Number(it.unit_price || 0),
          vat_rate: Number(it.vat_rate || 0),
        }))
      : [{ product_id: null, project_item_id: null, name: "", quantity: 1, unit: "adet", unit_price: 0, vat_rate: 0 }]
  );
  const [discountType, setDiscountType] = useState("none"); // "none" | "percent" | "amount"
  const [discountValue, setDiscountValue] = useState(0);
  const [discountBase, setDiscountBase] = useState("net"); // "net" (KDV hariç) | "gross" (KDV dahil)

  useEffect(() => {
    api.get(cfg.entityEndpoint).then(r=>setEntities(r.data));
    api.get("/products").then(r=>setProducts(r.data));
    if (id) {
      api.get(`${cfg.endpoint}/${id}`).then(r=>{
        const inv = r.data;
        setEntityId(inv.entity_id);
        setProjectId(inv.project_id || "");
        setCurrency(inv.currency);
        setDate(inv.date?.slice(0,16) || new Date().toISOString().slice(0,16));
        setNotes(inv.notes || "");
        setItems(inv.items.map(it=>({
          product_id: it.product_id, project_item_id: it.project_item_id || null,
          name: it.name, quantity: it.quantity, unit: it.unit,
          unit_price: it.unit_price, vat_rate: it.vat_rate
        })));
        setDiscountType(inv.discount_type || "none");
        setDiscountValue(inv.discount_value || 0);
        setDiscountBase(inv.discount_base || "net");
      });
    }
    // eslint-disable-next-line
  }, [id, kind]);

  // Load projects for the selected customer (sales only)
  useEffect(() => {
    if (kind === "sales" && entityId) {
      api.get("/projects", { params: { customer_id: entityId } }).then(r => setProjects(r.data));
    } else {
      setProjects([]);
    }
  }, [kind, entityId]);

  const setItem = (idx, key, val) => setItems(items.map((it,i)=> i===idx ? { ...it, [key]: val } : it));
  const addItem = () => setItems([...items, { product_id: null, name: "", quantity: 1, unit: "adet", unit_price: 0, vat_rate: 0 }]);
  const removeItem = (idx) => setItems(items.filter((_,i)=>i!==idx));

  const pickProduct = (idx, pid) => {
    const p = products.find(x=>x.id===pid);
    if (p) setItems(items.map((it,i)=> i===idx ? { ...it, product_id: p.id, name: p.name, unit: p.unit, unit_price: p.price, vat_rate: p.vat_rate } : it));
  };

  const subtotal = items.reduce((s,it)=> s + Number(it.quantity||0)*Number(it.unit_price||0), 0);
  const grossSubtotal = items.reduce((s,it)=> {
    const line = Number(it.quantity||0) * Number(it.unit_price||0);
    return s + line * (1 + Number(it.vat_rate||0)/100);
  }, 0);
  const dVal = Number(discountValue || 0);
  let ratio = 1;
  if (discountType === "percent") {
    ratio = 1 - Math.max(0, Math.min(100, dVal)) / 100;
  } else if (discountType === "amount") {
    const baseTotal = discountBase === "gross" ? grossSubtotal : subtotal;
    const clamped = Math.max(0, Math.min(baseTotal, dVal));
    ratio = baseTotal > 0 ? 1 - clamped / baseTotal : 1;
  }
  const discountedSubtotal = subtotal * ratio;
  const vat = items.reduce((s,it)=> s + Number(it.quantity||0)*Number(it.unit_price||0) * ratio * (Number(it.vat_rate||0)/100), 0);
  const total = discountedSubtotal + vat;
  const discountAmountNet = subtotal - discountedSubtotal;
  const discountAmountGross = grossSubtotal - total;

  const save = async () => {
    if (!entityId) { toast.error(`${cfg.entityLabel} seçin`); return; }
    if (items.length===0 || !items[0].name) { toast.error("En az bir ürün ekleyin"); return; }
    const now = new Date();
    const dateStr = date.length <= 16
      ? new Date(`${date}:${String(now.getSeconds()).padStart(2,"0")}`).toISOString()
      : new Date(date).toISOString();
    const body = {
      entity_id: entityId,
      date: dateStr,
      currency,
      notes,
      project_id: projectId || null,
      items: items.map(it=>({ ...it, quantity: Number(it.quantity), unit_price: Number(it.unit_price), vat_rate: Number(it.vat_rate) })),
      discount_type: discountType,
      discount_value: Number(discountValue || 0),
      discount_base: discountBase,
    };
    try {
      const url = id ? `${cfg.endpoint}/${id}` : cfg.endpoint;
      const method = id ? api.put : api.post;
      const r = await method(url, body);
      toast.success("Fatura kaydedildi");
      nav(`${cfg.listRoute}/${r.data.id}`, { state: { returnTo: backTo } });
    } catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); }
  };

  return (
    <div className="space-y-6" data-testid={`${kind}-invoice-form`}>
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={()=>nav(backTo)} data-testid="invoice-form-back"><ArrowLeft size={16}/></Button>
        <div><div className="text-xs uppercase tracking-widest text-zinc-500">{id?"Düzenle":"Yeni"}</div><h1 className="text-3xl mt-1">{cfg.title}</h1></div>
      </div>

      <div className="bg-white p-6 border border-zinc-200 rounded-md grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-2">
          <Label>{cfg.entityLabel}</Label>
          <Select value={entityId} onValueChange={setEntityId}>
            <SelectTrigger data-testid="invoice-entity-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
            <SelectContent>{entities.map(e=><SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Tarih & Saat</Label><Input type="datetime-local" value={date} onChange={(e)=>setDate(e.target.value)} /></div>
        <div>
          <Label>Para Birimi</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{CURRENCIES.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {kind === "sales" && (
          <div className="md:col-span-2">
            <Label>Proje (opsiyonel)</Label>
            <Select value={projectId || "none"} onValueChange={(v)=>setProjectId(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="invoice-project-select"><SelectValue placeholder="Proje seçin" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Proje bağlama —</SelectItem>
                {projects.map(p=><SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="bg-white p-4 border border-zinc-200 rounded-md">
        <table className="acc">
          <thead><tr><th style={{width:200}}>Ürün</th><th>Ad</th><th className="num" style={{width:100}}>Miktar</th><th style={{width:110}}>Birim</th><th className="num" style={{width:130}}>Birim Fiyat</th><th className="num" style={{width:100}}>KDV%</th><th className="num" style={{width:130}}>Toplam</th><th></th></tr></thead>
          <tbody>
            {items.map((it, idx) => {
              const line = Number(it.quantity||0) * Number(it.unit_price||0);
              const withVat = line * (1 + Number(it.vat_rate||0)/100);
              return (
                <tr key={idx}>
                  <td>
                    <Select value={it.product_id || ""} onValueChange={(v)=>pickProduct(idx, v)}>
                      <SelectTrigger data-testid={`item-product-${idx}`}><SelectValue placeholder="Katalogdan" /></SelectTrigger>
                      <SelectContent>{products.map(p=><SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td><Input data-testid={`item-name-${idx}`} value={it.name} onChange={(e)=>setItem(idx, "name", e.target.value)} /></td>
                  <td><Input data-testid={`item-qty-${idx}`} type="number" step="0.01" className="text-right" value={it.quantity} onChange={(e)=>setItem(idx, "quantity", e.target.value)} /></td>
                  <td>
                    <Select value={it.unit} onValueChange={(v)=>setItem(idx, "unit", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{UNITS.map(u=><SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td><Input data-testid={`item-price-${idx}`} type="number" step="0.01" className="text-right" value={it.unit_price} onChange={(e)=>setItem(idx, "unit_price", e.target.value)} /></td>
                  <td>
                    <Select value={String(it.vat_rate)} onValueChange={(v)=>setItem(idx, "vat_rate", Number(v))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{VAT_RATES.map(v=><SelectItem key={v} value={String(v)}>{v}%</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="num">{formatMoney(withVat, currency)}</td>
                  <td><button onClick={()=>removeItem(idx)} className="text-zinc-400 hover:text-red-600"><Trash2 size={14}/></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Button variant="ghost" size="sm" onClick={addItem} className="mt-2" data-testid="add-item-button"><Plus size={14} className="mr-1"/>Satır ekle</Button>
      </div>

      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div className="flex-1">
          <Label>Not</Label>
          <Textarea value={notes} onChange={(e)=>setNotes(e.target.value)} rows={2} />
        </div>
        <div className="w-full md:w-72 bg-white border border-zinc-200 rounded-md p-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-zinc-500">Ara Toplam</span><span className="number">{formatMoney(subtotal, currency)}</span></div>
          <div className="pt-2 border-t border-zinc-200 space-y-2">
            <div className="text-xs uppercase tracking-widest text-zinc-500">İskonto</div>
            <div className="flex gap-2">
              <Select value={discountType} onValueChange={(v)=>{ setDiscountType(v); if(v==="none") setDiscountValue(0); }}>
                <SelectTrigger data-testid="discount-type-select" className="w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Yok</SelectItem>
                  <SelectItem value="percent">%</SelectItem>
                  <SelectItem value="amount">Tutar</SelectItem>
                </SelectContent>
              </Select>
              <Input
                data-testid="discount-value-input"
                type="number"
                step="0.01"
                min="0"
                disabled={discountType === "none"}
                className="text-right"
                value={discountValue}
                onChange={(e)=>setDiscountValue(e.target.value)}
              />
            </div>
            {discountType === "amount" && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Uygulama Bazı</div>
                <Select value={discountBase} onValueChange={setDiscountBase}>
                  <SelectTrigger data-testid="discount-base-select" className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="net">KDV Hariç (Ara Toplamdan)</SelectItem>
                    <SelectItem value="gross">KDV Dahil (Genel Toplamdan)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {discountAmountNet > 0.005 && (
              <div className="space-y-0.5" data-testid="discount-amount-display">
                <div className="flex justify-between text-zinc-600">
                  <span>İskonto (KDV hariç)</span>
                  <span className="number">-{formatMoney(discountAmountNet, currency)}</span>
                </div>
                {discountType === "amount" && discountBase === "gross" && (
                  <div className="flex justify-between text-zinc-500 text-xs">
                    <span>İskonto (KDV dahil etki)</span>
                    <span className="number">-{formatMoney(discountAmountGross, currency)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-between pt-2 border-t border-zinc-200"><span className="text-zinc-500">KDV</span><span className="number">{formatMoney(vat, currency)}</span></div>
          <div className="flex justify-between text-base font-medium pt-1 border-t border-zinc-200"><span>Genel Toplam</span><span className="number" data-testid="invoice-grand-total">{formatMoney(total, currency)}</span></div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={()=>nav(backTo)}>İptal</Button>
        <Button data-testid="save-invoice-button" onClick={save} className="bg-zinc-900 hover:bg-zinc-800">Kaydet</Button>
      </div>
    </div>
  );
}
